/**
 * Takeoff quantities and on-plan label text, ported from TakeoffDocument
 * (`realWorldValue`, `quantityText`, `onPlanLabelText`, `DepthSpec`).
 */
import type { PageScale } from "../types.js";
import type { Point } from "../scale.js";
import { pathLength, quickMeasureLabel, ringArea } from "../scale.js";
import { areaFromSquareFeet, linearFromFeet } from "../units.js";
import type { AppearanceColor, DepthSpec, Markup, Measurement, MeasurementLabelStyle, MeasurementKind, QuickMeasureStyle, Vec2 } from "./types.js";

const formatters = new Map<number, Intl.NumberFormat>();
/** `formattedQuantity`: NumberFormatter decimal, grouping, 0…n fraction digits, half-even. */
export function formattedQuantity(value: number, unit: string, fractionDigits = 2): string {
  const digits = Math.max(0, fractionDigits);
  let f = formatters.get(digits);
  if (!f) {
    f = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: digits, useGrouping: true, roundingMode: "halfEven" } as Intl.NumberFormatOptions);
    formatters.set(digits, f);
  }
  return `${f.format(value)} ${unit}`;
}

export const toPoint = ([x, y]: Vec2): Point => ({ x, y });
export const toPoints = (v: readonly Vec2[]): Point[] => v.map(toPoint);

export function defaultMeasurementColor(kind: MeasurementKind): AppearanceColor {
  switch (kind) { case "linear": return "green"; case "arrow": return "red"; case "area": return "blue"; case "count": return "orange"; }
}
export const effectiveMeasurementColor = (m: Pick<Measurement, "kind" | "color">): AppearanceColor => m.color ?? defaultMeasurementColor(m.kind);

export const DEFAULT_LABEL_STYLE: MeasurementLabelStyle = { rotationDegrees: 0, backgroundColor: "white", textColor: "ink", showBackground: true };
export const effectiveLabelStyle = (m: Pick<Measurement, "labelStyle">): MeasurementLabelStyle => m.labelStyle ?? DEFAULT_LABEL_STYLE;
export const labelFontSize = (s: MeasurementLabelStyle): number => Math.min(72, Math.max(4, s.fontSize ?? 9));
export const labelVisible = (s: MeasurementLabelStyle): boolean => !(s.isHidden ?? false);

export const DEFAULT_ASPHALT_PCF = 145;
export function depthPCF(d: DepthSpec): number {
  if (d.tonsPerCubicYard != null && d.tonsPerCubicYard > 0) return (d.tonsPerCubicYard * 2_000) / 27;
  return d.poundsPerCubicFoot ?? DEFAULT_ASPHALT_PCF;
}
export function depthQuantity(d: DepthSpec, squareFeet: number): number {
  const cubicFeet = squareFeet * (d.unit === "in" ? d.value / 12 : d.value);
  switch (d.output) {
    case "CF": return cubicFeet;
    case "CY": return cubicFeet / 27;
    case "TN": return (cubicFeet * depthPCF(d)) / 2_000;
  }
}

export function rawLengthPts(m: Pick<Measurement, "points">): number { return pathLength(toPoints(m.points), false); }
/** Net plan area: outline less every cutout, never negative. */
export function rawAreaPts2(m: Pick<Measurement, "points" | "cutouts">): number {
  const gross = ringArea(toPoints(m.points));
  if (!(gross > 0)) return 0;
  const holes = (m.cutouts ?? []).reduce((t, r) => t + ringArea(toPoints(r)), 0);
  return Math.max(0, gross - holes);
}

export function realWorldValue(m: Measurement, scale: PageScale | null | undefined): { value: number; unit: string } | null {
  if (m.manualQuantity != null) return { value: m.manualQuantity, unit: m.manualUnit ?? "EA" };
  switch (m.kind) {
    case "count": return { value: m.points.length, unit: "EA" };
    case "arrow": return null;
    case "linear": {
      if (!scale) return null;
      return { value: linearFromFeet(m.linearUnit, rawLengthPts(m) * scale.feetPerPoint), unit: m.linearUnit };
    }
    case "area": {
      if (!scale) return null;
      const sf = rawAreaPts2(m) * scale.feetPerPoint * scale.feetPerPoint;
      if (m.depth && m.depth.value > 0) return { value: depthQuantity(m.depth, sf), unit: m.depth.output };
      return { value: areaFromSquareFeet(m.areaUnit, sf), unit: m.areaUnit };
    }
  }
}

export function quantityText(m: Measurement, scale: PageScale | null | undefined): string | null {
  const v = realWorldValue(m, scale);
  if (!v) return null;
  let digits: number;
  if (m.manualQuantity != null) digits = Math.round(m.manualQuantity) === m.manualQuantity ? 0 : 2;
  else if (m.kind === "count") digits = 0;
  else if (m.kind === "linear" && m.linearUnit === "GM") digits = 4;
  else digits = 2;
  return formattedQuantity(v.value, v.unit, digits);
}

/** Text of a takeoff's on-plan label: description, then quantity. nil for counts. */
export function onPlanLabelText(m: Measurement, scale: PageScale | null | undefined): string | null {
  if (m.kind === "count" || !labelVisible(effectiveLabelStyle(m))) return null;
  const description = m.label.trim();
  const q = quantityText(m, scale);
  if (!description) return q;
  return q == null ? description : `${description}\n${q}`;
}

/** Vertices of a quick measure (`QuickMeasure.vertices(of:)`). */
export function quickMeasureVertices(m: Markup): Vec2[] {
  if (m.vertices) return m.vertices;
  const [x, y, w, h] = m.bounds;
  return [[x, y + h / 2], [x + w, y + h / 2]];
}

export function quickMeasureMarkupLabel(m: Markup, scale: PageScale | null | undefined): string | null {
  if (m.kind !== "quickMeasure") return null;
  const style = m.style as QuickMeasureStyle;
  return quickMeasureLabel(toPoints(quickMeasureVertices(m)), style, scale?.feetPerPoint);
}
